import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, Plus } from "lucide-react";

export default function ChapterPlanTable({ chapters, onChange, lessonsPerHour = 3 }) {
    const updateRow = (idx, patch) => {
        onChange(chapters.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
    };
    const removeRow = (idx) => {
        onChange(chapters.filter((_, i) => i !== idx));
    };
    const addRow = () => {
        onChange([...chapters, { title: "", hours: 2 }]);
    };

    const totalHours = chapters.reduce((s, c) => s + (Number(c.hours) || 0), 0);
    const estLessons = chapters.reduce(
        (s, c) => s + Math.max(1, Math.round((Number(c.hours) || 0) * lessonsPerHour)),
        0,
    );

    return (
        <div className="space-y-3" data-testid="chapter-plan-table">
            <div className="flex items-center justify-between">
                <div>
                    <div className="font-display text-lg text-primary">Chapter plan</div>
                    <p className="text-sm text-muted-foreground">
                        Tell the AI how to structure the program. It will generate ~
                        {lessonsPerHour} lessons per hour per chapter, plus a 20-question quiz.
                    </p>
                </div>
                <div className="text-xs text-muted-foreground text-right">
                    <div>
                        <span className="font-semibold text-foreground">{chapters.length}</span>{" "}
                        chapters
                    </div>
                    <div>
                        <span className="font-semibold text-foreground">{totalHours}</span> total
                        hours
                    </div>
                    <div>
                        ≈{" "}
                        <span className="font-semibold text-foreground">{estLessons}</span> lessons
                    </div>
                </div>
            </div>

            <div className="border border-border rounded-sm overflow-hidden">
                <div className="grid grid-cols-[auto_1fr_120px_auto] gap-2 items-center px-3 py-2 text-xs uppercase tracking-wider text-muted-foreground bg-secondary/40 border-b border-border">
                    <div className="w-6">#</div>
                    <Label className="text-xs">Chapter title</Label>
                    <Label className="text-xs">Hours</Label>
                    <div className="w-10" />
                </div>
                {chapters.map((c, idx) => (
                    <div
                        key={idx}
                        className="grid grid-cols-[auto_1fr_120px_auto] gap-2 items-center px-3 py-2 border-b border-border last:border-b-0 bg-card"
                        data-testid={`plan-row-${idx}`}
                    >
                        <div className="w-6 text-sm text-muted-foreground">{idx + 1}.</div>
                        <Input
                            value={c.title}
                            onChange={(e) => updateRow(idx, { title: e.target.value })}
                            placeholder={`e.g. Chapter ${idx + 1} title`}
                            data-testid={`plan-title-${idx}`}
                        />
                        <Input
                            type="number"
                            step="0.5"
                            min="0.5"
                            value={c.hours}
                            onChange={(e) =>
                                updateRow(idx, { hours: Number(e.target.value) })
                            }
                            data-testid={`plan-hours-${idx}`}
                        />
                        <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => removeRow(idx)}
                            disabled={chapters.length <= 1}
                            data-testid={`plan-remove-${idx}`}
                            aria-label="Remove chapter"
                        >
                            <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                    </div>
                ))}
            </div>
            <Button
                variant="outline"
                size="sm"
                onClick={addRow}
                data-testid="plan-add-row"
            >
                <Plus className="h-3 w-3 mr-1" />
                Add chapter
            </Button>
        </div>
    );
}
